import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  Chat,
  Message,
  Event,
  ChatMessage,
  UsedInsight,
  EventMessage,
} from '@microsoft/microsoft-graph-types';
import { Cache } from '../cache.decorator';
import { type CallRecords } from '@microsoft/microsoft-graph-types';
import { environment } from '../../../environments/environment';
import { rxResource } from '@angular/core/rxjs-interop';

export interface ExtendedChatMessage extends ChatMessage {
  chat: Chat;
}
interface ResolvedMember {
  userId: string;
  email: string;
  displayName: string;
}
@Injectable({
  providedIn: 'root',
})
export class MicrosoftGraphService {
  graphEndpoint: string = 'https://graph.microsoft.com/v1.0';
  betaGraphEndpoint: string = 'https://graph.microsoft.com/beta';
  resolvedMembers: ResolvedMember[] = [];
  apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient) { }

  @Cache()
  getSentEmails(day: Date): Observable<EventMessage[]> {
    const params = new HttpParams()
      .set(
        '$filter',
        `(sentDateTime ge ${formatISO(startOfDay(day))}) and (sentDateTime le ${formatISO(endOfDay(day))})`,
      )
      .set(
        '$select',
        'sentDateTime,subject,bodyPreview,sender,from,toRecipients,ccRecipients',
      );

    return this.fetchAllPages<Message>(`${this.graphEndpoint}/me/mailFolders/SentItems/messages`, params)
      .pipe(
        map((messages) =>
          messages.filter((m) => !m.subject?.startsWith('Accepted:')),
        ),
      );
  }

  private fetchAllPages<T>(url: string, params?: HttpParams): Observable<T[]> {
    const options = params ? { params } : {};

    return this.http.get(url, options).pipe(
      catchError((err: HttpErrorResponse) => {
        throw 'Error fetching MS Graph Details: ' + err.message;
      }),
      mergeMap((response: any) => {
        const items = response.value as T[];
        const nextLink = response['@odata.nextLink'];

        if (nextLink) {
          // Recursively fetch next page (nextLink already contains params)
          return this.fetchAllPages<T>(nextLink).pipe(
            map((nextItems) => [...items, ...nextItems])
          );
        }

        return of(items);
      })
    );
  }

  @Cache()
  getEmails(day: Date): Observable<Message[]> {
    const params = new HttpParams()
      .set(
        '$filter',
        `(receivedDateTime ge ${formatISO(startOfDay(day))}) and (receivedDateTime le ${formatISO(endOfDay(day))})`,
      )
      .set(
        '$select',
        'receivedDateTime,subject,bodyPreview,sender,from,toRecipients,ccRecipients',
      );

    return this.fetchAllPages<Message>(`${this.graphEndpoint}/me/messages`, params);
  }

  @Cache()
  getCalendar(day: Date): Observable<Event[]> {
    const params = new HttpParams()
      .set('startDateTime', `${formatISO(startOfDay(day))}`)
      .set('endDateTime', `${formatISO(endOfDay(day))}`)
      .set(
        '$select',
        'subject,bodyPreview,webLink,start,end,attendees,organizer,isAllDay,onlineMeeting',
      );

    return this.fetchAllPages<Event>(`${this.graphEndpoint}/me/calendarView`, params);
  }

  getAllChatMessages(day: Date): Observable<ExtendedChatMessage[]> {
    const params = new HttpParams()
      .set(
        '$select',
        'id,chatType,topic,onlineMeetingInfo,createdDateTime,lastUpdatedDateTime,webUrl',
      )
      .set('$orderBy', 'lastMessagePreview/createdDateTime desc')
      .set('$expand', 'members,lastMessagePreview')
      .set('$top', '50') // Only fetch recent chats
      .set('$filter', `lastMessagePreview/createdDateTime ge ${formatISO(startOfDay(day))} and lastMessagePreview/createdDateTime le ${formatISO(endOfDay(day))}`);

    return this.http.get(`${this.betaGraphEndpoint}/me/chats`, { params })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          throw 'Error fetching MS Graph Details: ' + err.message;
        }),
        map((results: any) => results.value as Chat[]),
        // Filter locally to only chats updated on the specified day
        // map((chats: Chat[]) => 
        //   chats.filter(chat => {
        //     if (!chat.lastMessagePreview) return false;
        //     const lastUpdated = new Date(chat.lastMessagePreview.createdDateTime || chat.lastUpdatedDateTime || '');
        //     return lastUpdated >= startTime && lastUpdated <= endTime;
        //   })
        // ),
        mergeMap((events: Chat[]) => {
          if (events.length === 0) {
            return of([]);
          }

          const messages: Observable<ExtendedChatMessage[]>[] = events.map(
            (chat) => {
              const params = new HttpParams().set(
                '$filter',
                `lastModifiedDateTime gt ${formatISO(startOfDay(day))} and lastModifiedDateTime lt ${formatISO(endOfDay(day))}`,
              );

              return this.fetchAllPages<ChatMessage>(`${this.graphEndpoint}/me/chats/${chat.id}/messages`, params)
                .pipe(
                  catchError(() => of([])),
                  map((messages) => {
                    return messages.map((message) => {
                      return {
                        ...message,
                        chat: chat,
                      };
                    });
                  }),
                );
            },
          );
          return forkJoin(messages).pipe(
            map((x) => x.reduce((arr, curr) => [...arr, ...curr])),
          );
        }),
      );
  }

  @Cache()
  getChatMessages(day: Date): Observable<ExtendedChatMessage[]> {
    return this.getAllChatMessages(day).pipe(
      map((messages) => {
        return messages.filter((m) => ['message'].includes(m.messageType!));
      }),
    );
  }

  @Cache()
  getInsights(day: Date): Observable<UsedInsight[]> {
    const params = new HttpParams().set(
      '$filter',
      `LastUsed/LastAccessedDateTime ge ${formatISO(startOfDay(day))} and LastUsed/LastAccessedDateTime le ${formatISO(endOfDay(day))}`,
    );
    return this.http
      .get(`${this.graphEndpoint}/me/insights/used`, {
        params,
      })
      .pipe(map((results: any) => results.value));
  }

  @Cache()
  getCalls(day: Date): Observable<CallRecords.CallRecord[]> {
    const params = new HttpParams().set('date', day.toISOString());

    return this.http
      .get<CallRecords.CallRecord[]>(`${this.apiUrl}/api/v1/ms/calls`, {
        params,
      });
  }

  @Cache()
  getProfile() {
    return this.http.get(`${this.graphEndpoint}`);
  }

  @Cache()
  getServiceHealth() {
    return this.http.get(
      'https://graph.microsoft.com/v1.0/admin/serviceAnnouncement/healthOverviews/Microsoft 365 suite',
    );
  }

  @Cache()
  getProfilePhoto(): Observable<string | null> {
    return this.http
      .get(`${this.graphEndpoint}/me/photo/$value`, { responseType: 'blob' })
      .pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError(() => of(null)),
      );
  }

  private readonly userPhotoUrlResource = rxResource({
    stream: () => this.getProfilePhoto(),
  });
  public readonly userPhotoUrl = this.userPhotoUrlResource.value;

  resolveUserId(userId: string): ResolvedMember | null {
    const member = this.resolvedMembers.find(
      (member) => member.userId === userId,
    );
    return member ? member : null;
  }
}
